import React from "react";
import { lightenColor } from "./chartTheme";
import { ChartDataPoint } from "../types";

// Constants for better maintainability
const DOT_RADIUS = 6;
const DOT_STROKE_WIDTH = 3;
const FILL_LIGHTEN_AMOUNT = 0.3;

/**
 * Props for CustomDot component
 */
interface CustomDotProps {
  cx: number;
  cy: number;
  payload: ChartDataPoint;
  dataKey?: string;
  viewMode?: string;
  getColorFn: (value: number) => string;
}

/**
 * Helper function to safely extract numeric value from payload
 */
const extractValue = (
  payload: ChartDataPoint,
  dataKey?: string
): number | null => {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  if (dataKey) {
    const value = payload[dataKey as keyof ChartDataPoint];
    return typeof value === "number" ? value : null;
  }

  // Find first numeric property (heartRate, systolic, or diastolic)
  if (typeof payload.heartRate === "number") {
    return payload.heartRate;
  }
  if (typeof payload.systolic === "number") {
    return payload.systolic;
  }
  if (typeof payload.diastolic === "number") {
    return payload.diastolic;
  }

  return null;
};

/**
 * Custom dot component for chart data points
 * Used to render individual data points with dynamic colors based on value
 */
export const CustomDot: React.FC<CustomDotProps> = ({
  cx,
  cy,
  payload,
  dataKey,
  viewMode,
  getColorFn,
}) => {
  // Filter dots based on view mode (for blood pressure chart)
  if (viewMode && dataKey) {
    if (viewMode === "systolic" && dataKey === "diastolic") return null;
    if (viewMode === "diastolic" && dataKey === "systolic") return null;
  }

  const value = extractValue(payload, dataKey);

  // Safety check: don't render if value is invalid
  if (value === null || !Number.isFinite(value)) {
    return null;
  }

  const strokeColor = getColorFn(value);
  const fillColor = lightenColor(strokeColor, FILL_LIGHTEN_AMOUNT);

  return (
    <circle
      cx={cx}
      cy={cy}
      r={DOT_RADIUS}
      fill={fillColor}
      stroke={strokeColor}
      strokeWidth={DOT_STROKE_WIDTH}
    />
  );
};

/**
 * Props for CustomActiveDot component
 */
interface CustomActiveDotProps {
  cx: number;
  cy: number;
  payload: ChartDataPoint;
  dataKey?: string;
  getColorFn: (value: number) => string;
}

/**
 * Custom active dot component for highlighted/hovered data points
 * Same as CustomDot but used when a point is actively selected
 */
export const CustomActiveDot: React.FC<CustomActiveDotProps> = ({
  cx,
  cy,
  payload,
  dataKey,
  getColorFn,
}) => {
  const value = extractValue(payload, dataKey);

  // Safety check: don't render if value is invalid
  if (value === null || !Number.isFinite(value)) {
    return null;
  }

  const strokeColor = getColorFn(value);
  const fillColor = lightenColor(strokeColor, FILL_LIGHTEN_AMOUNT);

  return (
    <circle
      cx={cx}
      cy={cy}
      r={DOT_RADIUS}
      fill={fillColor}
      stroke={strokeColor}
      strokeWidth={DOT_STROKE_WIDTH}
    />
  );
};
